// Parser for `rtk discover` output. Isolated here so a future `rtk discover
// --json` is a one-function change and never touches the provider/engine.
//
// rtk has no JSON flag yet; we parse the "MISSED SAVINGS" table. Example rows:
//   Command                  Count    RTK Equivalent     Status        Est. Savings
//   head -8                     22    rtk read           existing      ~11.9K tokens

export interface RtkFinding {
  command: string;
  count: number;
  equivalent: string;
  /** Estimated tokens saved (absolute number, e.g. 11900 for "~11.9K tokens"). */
  estSaving: number;
}

/** Parse the savings rows out of `rtk discover` text. Skips unparseable rows. */
export function parseRtkDiscover(text: string): RtkFinding[] {
  const lines = text.split("\n");
  const findings: RtkFinding[] = [];

  let inSavings = false;
  for (const line of lines) {
    // Enter the savings section; leave it at the next section header / total.
    if (/^MISSED SAVINGS/i.test(line)) { inSavings = true; continue; }
    if (inSavings && /^(TOP UNHANDLED|Total:|=)/i.test(line.trim())) break;
    if (!inSavings) continue;

    const row = parseRow(line);
    if (row) findings.push(row);
  }

  return findings;
}

/** Parse one table row, or null if it's a header/divider/blank. */
function parseRow(line: string): RtkFinding | null {
  if (!line.trim()) return null;
  if (/^[-─=]+$/.test(line.trim())) return null;          // divider
  if (/^Command\s+Count/i.test(line.trim())) return null; // header

  // Columns are whitespace-separated; the command itself may contain spaces, so
  // anchor on: <command...> <count:int> <equivalent...> <status> ~<saving>.
  const m = line.match(
    /^(.+?)\s{2,}(\d+)\s+(.+?)\s{2,}\S+\s+~?([\d.]+)\s*([KkMm]?)\s*tokens?/
  );
  if (!m) return null;

  const [, command, count, equivalent, num, unit] = m;
  return {
    command: command.trim(),
    count: Number(count),
    equivalent: equivalent.trim(),
    estSaving: scaleTokens(Number(num), unit),
  };
}

function scaleTokens(num: number, unit: string): number {
  const u = unit.toLowerCase();
  if (u === "k") return Math.round(num * 1_000);
  if (u === "m") return Math.round(num * 1_000_000);
  return Math.round(num);
}
